import { DateTime } from 'luxon';
import { action, computed, makeObservable, reaction } from 'mobx';

import { draftTimelogsApi } from '@/api/draftTimelogsApi';
import { timelogsApi } from '@/api/timelogsApi';
import { AlertsStore } from '@/configs/store/AlertsStore';
import { ViewTable } from '@/configs/ViewTable';
import { yaMetrikaStore } from '@/entities/yaMetrika';
import { isLogsReadOnly, LogsMode } from '@/features/logsMode';
import { viewSettingsStore } from '@/features/viewSettings';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { TimeLogStore } from '@/pages/TimesheetsPage/model/TimeLogStore';
import { DraftTimelog, WeeklyWorkLog } from '@/types';
import { BatchPublishDraftsResult, DraftSkipReason } from '@/types/timelog';
import { AnalyticsEvent } from '@/types/yametrika';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { formatError } from '@/utils/formatError';
import { getCalendarRange } from '@/utils/getCalendarRange';
import { parseDuration } from '@/utils/parseDuration';
import { transformTimelogToWeeklyWorkLog } from '@/utils/transformTimelog';

type DraftFilter = 'all' | 'logs' | 'drafts';

const SKIP_REASON_LABEL: Record<DraftSkipReason, string> = {
  draft_without_task: 'без задачи',
  task_not_found: 'задачи нет в нашем учёте',
  queue_access_denied: 'нет доступа к очереди',
  invalid_duration: 'длительность больше 24 часов',
  draft_timelog_not_found: 'черновик уже удалён или опубликован',
};

/** «Пропущено: 3 (без задачи — 1, длительность больше 24 часов — 2)» */
const formatSkippedDrafts = (skipped: BatchPublishDraftsResult['skipped']): string => {
  const counts = new Map<string, number>();

  skipped.forEach(({ reason }) => {
    // Новую причину бэка показываем как есть, а не теряем
    const label = SKIP_REASON_LABEL[reason] ?? reason;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  });

  const details = [...counts].map(([label, count]) => `${label} — ${count}`).join(', ');

  return `Пропущено: ${skipped.length} (${details})`;
};

type DraftTimelogInput = {
  duration: string;
  start: string;
  comment?: string;
  taskKey?: string | null;
  taskTitle?: string | null;
  queueKey?: string | null;
  queueName?: string | null;
};

export class DraftTimelogStore extends LocalStore {
  private readonly _timeLogStore: TimeLogStore;
  private readonly _alertsStore: AlertsStore;

  private readonly _drafts = new ValueModel<DraftTimelog[]>([]);
  private readonly _loadingStage = new LoadingStageModel();
  private readonly _publishStage = new LoadingStageModel();
  private readonly _draftFilter = new ValueModel<DraftFilter>('all');

  constructor({
    timeLogStore,
    alertsStore,
  }: {
    timeLogStore: TimeLogStore;
    alertsStore: AlertsStore;
  }) {
    super();
    this._timeLogStore = timeLogStore;
    this._alertsStore = alertsStore;

    makeObservable(this, {
      drafts: computed,
      loadingStage: computed,
      publishStage: computed,
      draftFilter: computed,
      draftsByDate: computed,
      filteredDraftsByDate: computed,
      draftMinutesByDate: computed,
      totalDraftMinutes: computed,
      unpublishedDraftCount: computed,
      hasDrafts: computed,
      isPeriodPast: computed,
      viewedDraftPeriodIso: computed,
      draftsWithoutTaskCount: computed,
      setDrafts: action.bound,
      setDraftFilter: action.bound,
      loadCurrentPeriodDrafts: action.bound,
      loadDrafts: action.bound,
      createDraft: action.bound,
      updateDraft: action.bound,
      deleteDraft: action.bound,
      publishDraft: action.bound,
      publishBulk: action.bound,
    });

    const periodReaction = reaction(
      () => [
        this._timeLogStore.filterData?.weekStart ?? null,
        viewSettingsStore.view,
        this._timeLogStore.selectedUserId,
        this.isViewingSelf,
      ],
      ([weekStart]) => {
        if (weekStart) {
          this.loadCurrentPeriodDrafts();
        }
      },
      { fireImmediately: true },
    );

    this.addReaction(periodReaction);
  }

  get drafts() {
    return this._drafts.value;
  }

  get loadingStage() {
    return this._loadingStage;
  }

  get publishStage() {
    return this._publishStage;
  }

  get draftFilter(): DraftFilter {
    return this._draftFilter.value;
  }

  get draftsByDate(): Record<string, DraftTimelog[]> {
    return this._drafts.value.reduce<Record<string, DraftTimelog[]>>((acc, draft) => {
      const dateKey = DateTime.fromISO(draft.start).toISODate();

      if (!dateKey) return acc;

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push(draft);
      acc[dateKey].sort((a, b) => a.createdAt.localeCompare(b.createdAt));

      return acc;
    }, {});
  }

  get filteredDraftsByDate(): Record<string, DraftTimelog[]> {
    if (this.draftFilter === 'logs') {
      return {};
    }

    return this.draftsByDate;
  }

  get draftMinutesByDate(): Record<string, number> {
    const result: Record<string, number> = {};

    for (const [date, drafts] of Object.entries(this.draftsByDate)) {
      result[date] = drafts.reduce((sum, d) => sum + d.durationMinutes, 0);
    }

    return result;
  }

  get totalDraftMinutes(): number {
    return this._drafts.value.reduce((sum, d) => sum + d.durationMinutes, 0);
  }

  get unpublishedDraftCount(): number {
    return this._drafts.value.length;
  }

  get hasDrafts(): boolean {
    return this.unpublishedDraftCount > 0;
  }

  /** Весь отображаемый период (неделя или сетка месяца) уже в прошлом */
  get isPeriodPast(): boolean {
    const weekEnd = this._timeLogStore.filterData?.weekEnd;

    if (!weekEnd) return false;

    const today = DateTime.local().startOf('day');
    const periodLastDay = DateTime.fromISO(weekEnd).startOf('day');

    return periodLastDay < today;
  }

  /** Диапазон дат для bulk-публикации (совпадает с загрузкой списка черновиков) */
  get viewedDraftPeriodIso(): { dateFrom: string; dateTo: string } {
    return this._resolvePeriod(this._timeLogStore.currentDate);
  }

  get draftsWithoutTaskCount(): number {
    return this._drafts.value.filter((d) => !d.taskKey).length;
  }

  get isViewingSelf(): boolean {
    return this._timeLogStore.usersFilterModel.isViewingSelf;
  }

  setDrafts(drafts: DraftTimelog[]) {
    this._drafts.change(drafts);
  }

  setDraftFilter(value: DraftFilter) {
    this._draftFilter.change(value);
  }

  private _normalizeDraft(draft: DraftTimelog): DraftTimelog {
    return {
      ...draft,
      durationMinutes: parseDuration(draft.duration),
    };
  }

  private _resolvePeriod(date: DateTime): { dateFrom: string; dateTo: string } {
    if (viewSettingsStore.view === ViewTable.Month) {
      const calendarRange = getCalendarRange(date);

      if (!calendarRange) {
        const start = date.startOf('month').startOf('day');
        const end = date.endOf('month').endOf('day');

        return { dateFrom: start.toISO()!, dateTo: end.toISO()! };
      }

      return {
        dateFrom: calendarRange.startOfCalendar.startOf('day').toISO()!,
        dateTo: calendarRange.endOfCalendar.endOf('day').toISO()!,
      };
    }

    const weekStart = date.startOf('week').startOf('day');
    const weekEnd = weekStart.plus({ days: 6 }).endOf('day');

    return {
      dateFrom: weekStart.toISO()!,
      dateTo: weekEnd.toISO()!,
    };
  }

  async loadCurrentPeriodDrafts() {
    // Пока собственный учёт только для просмотра, черновиков (их создают и публикуют) на его экранах нет
    if (!this.isViewingSelf || isLogsReadOnly(this._timeLogStore.mode)) {
      this.setDrafts([]);
      this._loadingStage.success();

      return;
    }

    const { dateFrom, dateTo } = this._resolvePeriod(this._timeLogStore.currentDate);
    await this.loadDrafts(dateFrom, dateTo);
  }

  async loadDrafts(dateFrom: string, dateTo: string) {
    try {
      this._loadingStage.loading();
      const drafts = await draftTimelogsApi.list({ dateFrom, dateTo });
      this.setDrafts(drafts.map((item) => this._normalizeDraft(item)));
      this._loadingStage.success();
    } catch (error) {
      this._loadingStage.error();
      const formatted = formatError(error);

      this._alertsStore.addAlert({
        title: formatted.title,
        message: formatted.message,
        withReload: false,
        autoClose: true,
      });
    }
  }

  async createDraft(data: DraftTimelogInput): Promise<DraftTimelog | null> {
    try {
      const created = await draftTimelogsApi.create(data);
      const normalized = this._normalizeDraft(created);
      this._drafts.change([...this._drafts.value, normalized]);
      yaMetrikaStore.sendEvent(AnalyticsEvent.draftCreate);

      return normalized;
    } catch (error) {
      const formatted = formatError(error);
      this._alertsStore.addAlert({
        title: formatted.title,
        message: formatted.message,
        withReload: false,
        autoClose: true,
      });

      return null;
    }
  }

  async updateDraft(id: string, data: Partial<DraftTimelogInput>): Promise<DraftTimelog | null> {
    try {
      const updated = await draftTimelogsApi.update(id, data);
      const normalized = this._normalizeDraft(updated);
      this._drafts.change(
        this._drafts.value.map((draft) => (draft.id === id ? normalized : draft)),
      );
      yaMetrikaStore.sendEvent(AnalyticsEvent.draftEdit);

      return normalized;
    } catch (error) {
      const formatted = formatError(error);
      this._alertsStore.addAlert({
        title: formatted.title,
        message: formatted.message,
        withReload: false,
        autoClose: true,
      });

      return null;
    }
  }

  async deleteDraft(id: string): Promise<void> {
    try {
      await draftTimelogsApi.remove(id);
      this._drafts.change(this._drafts.value.filter((draft) => draft.id !== id));
      yaMetrikaStore.sendEvent(AnalyticsEvent.draftDelete);
    } catch (error) {
      const formatted = formatError(error);
      this._alertsStore.addAlert({
        title: formatted.title,
        message: formatted.message,
        withReload: false,
        autoClose: true,
      });
    }
  }

  /**
   * Публикует черновик в Трекер (legacy) или в собственный учёт (v2).
   * На экране лог появляется, только если опубликован в систему, которую экран показывает
   */
  async publishDraft(id: string, target: LogsMode = this._timeLogStore.mode): Promise<boolean> {
    this._publishStage.loading();

    try {
      const draft = this._drafts.value.find((d) => d.id === id);

      if (target === LogsMode.v2) {
        const timelog = await timelogsApi.publishDraft(id);
        this._drafts.change(this._drafts.value.filter((d) => d.id !== id));
        this._publishStage.success();
        this._addShownLog({ log: transformTimelogToWeeklyWorkLog(timelog), target });
        yaMetrikaStore.sendEvent(AnalyticsEvent.draftPublish);

        return true;
      }

      const result = await draftTimelogsApi.publish(id);
      this._drafts.change(this._drafts.value.filter((d) => d.id !== id));
      this._publishStage.success();

      if (draft && draft.taskKey && this._timeLogStore.mode === LogsMode.legacy) {
        this._addOptimisticLog({
          worklogId: result.worklogId,
          taskKey: draft.taskKey,
          taskTitle: draft.taskTitle ?? '',
          duration: draft.duration,
          durationMinutes: draft.durationMinutes,
          start: draft.start,
          comment: draft.comment,
        });
      }

      yaMetrikaStore.sendEvent(AnalyticsEvent.draftPublish);

      return true;
    } catch (error) {
      this._publishStage.error();
      const formatted = formatError(error);
      this._alertsStore.addAlert({
        title: formatted.title,
        message: formatted.message,
        withReload: false,
        autoClose: true,
      });

      return false;
    }
  }

  async publishBulk(dateFrom: string, dateTo: string, target: LogsMode = this._timeLogStore.mode) {
    this._publishStage.loading();

    try {
      if (target === LogsMode.v2) {
        await this._publishBulkV2({ dateFrom, dateTo });

        return;
      }

      const result = await draftTimelogsApi.publishBulk({ dateFrom, dateTo });
      await this.loadDrafts(dateFrom, dateTo);
      this._publishStage.success();

      for (const wl of this._timeLogStore.mode === LogsMode.legacy ? result.worklogs : []) {
        const durationMinutes = parseDuration(wl.duration);
        this._addOptimisticLog({
          worklogId: wl.worklogId,
          taskKey: wl.taskKey,
          taskTitle: wl.taskTitle ?? '',
          duration: wl.duration,
          durationMinutes,
          start: wl.start,
          comment: wl.comment,
        });
      }

      yaMetrikaStore.sendEvent(AnalyticsEvent.draftPublishBulk, { count: result.worklogs.length });
    } catch (error) {
      this._publishStage.error();
      const formatted = formatError(error);
      this._alertsStore.addAlert({
        title: formatted.title,
        message: formatted.message,
        withReload: false,
        autoClose: true,
      });
    }
  }

  private async _publishBulkV2({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }) {
    const { published, skipped } = await timelogsApi.publishDrafts({ from: dateFrom, to: dateTo });
    await this.loadDrafts(dateFrom, dateTo);
    this._publishStage.success();

    published.forEach(({ timelog }) =>
      this._addShownLog({ log: transformTimelogToWeeklyWorkLog(timelog), target: LogsMode.v2 }),
    );

    if (skipped.length > 0) {
      this._alertsStore.addAlert({
        title: 'Опубликованы не все черновики',
        message: formatSkippedDrafts(skipped),
        withReload: false,
        autoClose: true,
      });
    }

    yaMetrikaStore.sendEvent(AnalyticsEvent.draftPublishBulk, { count: published.length });
  }

  /** Лог из собственного учёта — на экран, если экран показывает этот учёт */
  private _addShownLog({ log, target }: { log: WeeklyWorkLog; target: LogsMode }) {
    if (target !== this._timeLogStore.mode) return;

    this._addOptimisticLog({
      worklogId: log.id,
      taskKey: log.taskKey,
      taskTitle: log.taskTitle,
      duration: log.duration,
      durationMinutes: log.durationMinutes,
      start: log.start,
      comment: log.comment ?? '',
    });
  }

  private _addOptimisticLog(params: {
    worklogId: number | string;
    taskKey: string;
    taskTitle: string;
    duration: string;
    durationMinutes: number;
    start: string;
    comment: string;
  }) {
    const current = this._timeLogStore.timesheetData;

    if (!current) return;

    const logDate = DateTime.fromISO(params.start).toISODate();

    if (!logDate) return;

    const newLog = {
      id: String(params.worklogId),
      taskKey: params.taskKey,
      taskTitle: params.taskTitle,
      duration: params.duration,
      durationMinutes: params.durationMinutes,
      comment: params.comment,
      date: logDate,
      createdAt: new Date().toISOString(),
      start: params.start,
    };

    const updatedDays = { ...current.days };

    if (updatedDays[logDate]) {
      updatedDays[logDate] = [...updatedDays[logDate], newLog];
    } else {
      updatedDays[logDate] = [newLog];
    }

    const totalMinutes = current.totalMinutes + params.durationMinutes;

    this._timeLogStore.updateTimesheetData({
      ...current,
      days: updatedDays,
      totalMinutes,
      totalFormatted: formatDurationRussian(totalMinutes),
    });
  }
}
